import type { ChangeEvent, FC, FormEvent } from "react";
import { useState, useCallback, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import axios from "axios";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Label } from "../../../components/ui/label";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  ArrowRight,
  Shield,
  AlertTriangle,
  WifiOff,
} from "lucide-react";
import { FcGoogle } from "react-icons/fc";
import { useToast } from "../../../hooks/use-toast";
import "./Login.css";
import baseURL from "../../../http";
import { accountHref, isReturnablePath, onInstitutePathSite } from "@/features/site/accountLinks";
import { signInWithGoogle } from "./Firebase_Config";
import { ACCESS_REFUSED_MESSAGE_KEY } from "@/services/tenantHostAccess";
import {
  ChooseContextDialog,
  NoAccessDialog,
  useCompleteSignIn,
} from "./ChooseContext";
import {
  clearLegacyReturnKeys,
  readLinkReturn,
  rememberLinkReturn,
} from "./linkReturn";
import { useBranding, useBrandLogo, useBrandName } from "@/contexts/BrandingContext";
import { getDeviceUid } from "@/lib/deviceId";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import {
  getAuth,
  signInWithEmailAndPassword,
  sendEmailVerification,
  User,
} from "firebase/auth";
// ---------- types ----------
interface LoginData {
  email: string;
  password: string;
}

interface ValidationErrors {
  email?: string;
  password?: string;
  general?: string;
}

// ---------- constants ----------
const RESEND_MS = 60_000; // throttle resend verification
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION = 110 * 60 * 1000; // 110 minutes
const ATTEMPTS_STORAGE_KEY = "loginAttempts";
const DEFAULT_REDIRECT_TO = "/admin_dashboard_page";

interface LoginProps {
  defaultRedirectTo?: string;
  signupPath?: string;
}

const sanitizeRedirectTarget = (
  target: string | null | undefined,
  fallback = DEFAULT_REDIRECT_TO
) => {
  if (!target) return fallback;

  try {
    if (target.startsWith("http://") || target.startsWith("https://")) {
      const url = new URL(target);
      if (url.origin !== window.location.origin) return fallback;
      target = `${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    return fallback;
  }

  if (!target.startsWith("/") || target.startsWith("//")) return fallback;
  // Account pages, and the website home (on an institute's domain that is the
  // public site, not the portal), are never where a sign-in should end.
  if (target.startsWith("/login") || target.startsWith("/signup") || !isReturnablePath(target)) return fallback;
  return target;
};

// ---------- component ----------
const Login: FC<LoginProps> = ({
  defaultRedirectTo = DEFAULT_REDIRECT_TO,
  signupPath = "/signup",
}) => {
  // Whose sign-in screen this is. On the platform domain the lookup finds no
  // tenant and every value falls back to the EduExpertAI defaults, so this page
  // is unchanged there.
  const { branding, isPlatform } = useBranding();
  const brandName = useBrandName();
  const { logoUrl: brandLogo, fallbackUrl: platformLogo } = useBrandLogo();
  const brandTagline = branding.tagline || "";
  // A tenant logo is a remote URL that can 404 long after it was saved. On the
  // error event we drop to the platform mark rather than to a blank square.
  const [brandLogoFailed, setBrandLogoFailed] = useState(false);
  // Signed in fine, but not a member of the institute whose domain this is:
  // the same "No access yet" screen as on our domain, naming the institute.
  const [tenantNoAccess, setTenantNoAccess] = useState<string | null | undefined>(undefined);

  const [loginData, setLoginData] = useState<LoginData>({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  // Google is the only sign-in offered on arrival; the email form is revealed
  // by an explicit choice. Mirrors the mobile SignIn screen, and for the same
  // reason: shown side by side, the two methods read as a question the user has
  // to answer ("can I still use my password?"), and people who cannot read the
  // labels comfortably were picking the form and failing on an address that was
  // never registered. One unmistakable button is not a decision.
  const [showEmailForm, setShowEmailForm] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<ValidationErrors>({});
  const [signupdata, setsignupdata] = useState<any>({}); // kept to match your Google flow variable
  const [emailNeedsVerification, setEmailNeedsVerification] =
    useState<User | null>(null);
  const [resendLeftMs, setResendLeftMs] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isAccountLocked, setIsAccountLocked] = useState(false);
  const [lockoutTimeLeft, setLockoutTimeLeft] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const cancelTokens = useRef<any[]>([]);
  const lastResendAtRef = useRef(0);
  const resendTimerRef = useRef<number | null>(null);
  const lockoutTimerRef = useRef<number | null>(null);

  /** The target this page was given — router state, `?redirect=`, or the legacy keys. */
  const readExplicitReturnTo = useCallback((): string | null => {
    const stateReturnTo = (location.state as { returnTo?: string } | null)?.returnTo;
    const queryReturnTo = new URLSearchParams(location.search).get("redirect");
    const legacyReturnTo =
      localStorage.getItem("isRoutingManual") === "true"
        ? localStorage.getItem("returnToInvitationUrl") ||
        localStorage.getItem("returnToAssessmentUrl")
        : null;

    return stateReturnTo || queryReturnTo || legacyReturnTo || null;
  }, [location.search, location.state]);

  const getPostLoginRedirect = useCallback(
    () =>
      sanitizeRedirectTarget(
        // A link target remembered by an earlier page stands in when this one
        // was opened without any — the verification email's new tab, or a 2FA
        // error path back to a bare /login. See linkReturn.
        readExplicitReturnTo() || readLinkReturn(),
        defaultRedirectTo
      ),
    [defaultRedirectTo, readExplicitReturnTo]
  );

  // Keep a link target this page was handed, for whichever tab finishes the
  // sign-in. Only an explicit one: re-remembering the stored target on every
  // visit would keep a stale hand-off alive forever.
  useEffect(() => {
    const explicit = readExplicitReturnTo();
    if (explicit) rememberLinkReturn(sanitizeRedirectTarget(explicit, defaultRedirectTo));
  }, [readExplicitReturnTo, defaultRedirectTo]);

  // ---- After sign-in: institute choice, no-access, or straight in ---------
  // One rule for Login, 2FA and Sign up — see useCompleteSignIn. An account
  // linked to no institute is stopped here UNLESS it is on its way back to one
  // of the shareable links, which exist for exactly those people.
  const {
    completeSignIn,
    resumeExistingSession,
    noAccessDialogProps,
    contextDialogProps,
  } = useCompleteSignIn({
    getReturnTarget: getPostLoginRedirect,
    onFinish: (target, notice) => {
      if (notice) toast(notice);
      clearLegacyReturnKeys();
      navigate(target, { replace: true });
    },
  });

  /** The completion path for BOTH login methods (email and Google). */
  const completeLogin = useCallback(
    (title: string, description: string, email?: string | null) =>
      completeSignIn(email, { title, description }),
    [completeSignIn]
  );

  // ---- Authentication check on page load ----
  // A session already in storage continues — through the same no-access rule
  // as a fresh sign-in, so a session opened through a link cannot be carried
  // into the app by opening /login directly.
  useEffect(() => {
    const checkExistingAuth = () => {
      try {
        // Check for valid authentication tokens
        const authToken = localStorage.getItem("authToken");
        const accessToken = localStorage.getItem("access_token");
        const tokenExpiration = localStorage.getItem("token_expiration");

        // If we have tokens, check if they're still valid
        if (authToken || accessToken) {
          // Check token expiration if available
          if (tokenExpiration) {
            const expirationTime = parseInt(tokenExpiration);
            const currentTime = Date.now();

            if (currentTime < expirationTime) {
              void resumeExistingSession();
              return;
            } else {
              // Token expired, clear it
              localStorage.removeItem("authToken");
              localStorage.removeItem("access_token");
              localStorage.removeItem("token_expiration");
              sessionStorage.removeItem("authToken");
              sessionStorage.removeItem("access_token");
              sessionStorage.removeItem("token_expiration");
            }
          } else {
            // No expiration info, assume token is valid and continue
            void resumeExistingSession();
            return;
          }
        }

        // Also check for other auth indicators
        const isAuthenticated = localStorage.getItem("isAuthenticated");
        const user = localStorage.getItem("user");

        if (isAuthenticated === "true" && user) {
          try {
            const userData = JSON.parse(user);
            if (userData && userData.id) {
              void resumeExistingSession();
              return;
            }
          } catch (error) {
            console.warn("Failed to parse user data:", error);
            // Clear invalid user data
            localStorage.removeItem("user");
            localStorage.removeItem("isAuthenticated");
          }
        }
      } catch (error) {
        console.warn("Failed to check existing authentication:", error);
      }
    };

    // Check authentication on component mount. `resumeExistingSession` is
    // stable, so this runs once rather than on every location change.
    checkExistingAuth();
  }, [resumeExistingSession]);

  // ---- Online/Offline detection ----
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // ---- Initial form state + lockout check ----
  useEffect(() => {
    // Always start with empty fields — beat browser autofill with a short delay
    const t = window.setTimeout(() => {
      setLoginData({ email: "", password: "" });
    }, 50);

    // Check for account lockout
    checkAccountLockout();

    return () => window.clearTimeout(t);
  }, []);

  // ---- Account lockout management ----
  const checkAccountLockout = () => {
    try {
      const attempts = localStorage.getItem(ATTEMPTS_STORAGE_KEY);
      if (attempts) {
        const data = JSON.parse(attempts);
        const now = Date.now();

        if (data.count >= MAX_LOGIN_ATTEMPTS && data.lockoutUntil > now) {
          setIsAccountLocked(true);
          setLockoutTimeLeft(data.lockoutUntil - now);
          startLockoutTimer(data.lockoutUntil);
        } else if (data.lockoutUntil <= now) {
          // Lockout expired, reset attempts
          localStorage.removeItem(ATTEMPTS_STORAGE_KEY);
          setIsAccountLocked(false);
          setLockoutTimeLeft(0);
        }
      }
    } catch (error) {
      console.warn("Failed to check account lockout:", error);
    }
  };

  const recordFailedAttempt = () => {
    try {
      const attempts = localStorage.getItem(ATTEMPTS_STORAGE_KEY);
      const now = Date.now();
      let data = { count: 0, lockoutUntil: 0 };

      if (attempts) {
        data = JSON.parse(attempts);
        if (data.lockoutUntil <= now) {
          data.count = 0;
        }
      }

      data.count += 1;

      if (data.count >= MAX_LOGIN_ATTEMPTS) {
        data.lockoutUntil = now + LOCKOUT_DURATION;
        setIsAccountLocked(true);
        setLockoutTimeLeft(LOCKOUT_DURATION);
        startLockoutTimer(data.lockoutUntil);

        toast({
          title: "Account Temporarily Locked",
          description:
            "Too many failed attempts. Please try again in 15 minutes.",
          variant: "destructive",
        });
      }

      localStorage.setItem(ATTEMPTS_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn("Failed to record failed attempt:", error);
    }
  };

  const clearFailedAttempts = () => {
    try {
      localStorage.removeItem(ATTEMPTS_STORAGE_KEY);
      setIsAccountLocked(false);
      setLockoutTimeLeft(0);
      if (lockoutTimerRef.current) {
        clearInterval(lockoutTimerRef.current);
      }
    } catch (error) {
      console.warn("Failed to clear failed attempts:", error);
    }
  };

  const startLockoutTimer = (lockoutUntil: number) => {
    if (lockoutTimerRef.current) clearInterval(lockoutTimerRef.current);

    lockoutTimerRef.current = window.setInterval(() => {
      const remaining = lockoutUntil - Date.now();
      setLockoutTimeLeft(remaining);

      if (remaining <= 0) {
        setIsAccountLocked(false);
        setLockoutTimeLeft(0);
        localStorage.removeItem(ATTEMPTS_STORAGE_KEY);
        if (lockoutTimerRef.current) {
          clearInterval(lockoutTimerRef.current);
        }
        toast({
          title: "Account Unlocked",
          description: "You can now try logging in again.",
        });
      }
    }, 1000);
  };

  // ---- Enhanced validation ----
  const normalizeEmail = (e: string) => e.trim().toLowerCase();

  const validateEmail = (email: string): string | undefined => {
    if (!email) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      return "Enter a valid email address";
    if (email.length > 254) return "Email address is too long";
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) return "Password is required";
    if (password.length < 6) return "Password must be at least 6 characters";
    if (password.length > 128) return "Password is too long";
    return undefined;
  };

  const validate = (): boolean => {
    const errors: ValidationErrors = {};
    const email = normalizeEmail(loginData.email);
    const password = loginData.password.trim();

    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);

    if (emailError) errors.email = emailError;
    if (passwordError) errors.password = passwordError;

    if (!isOnline) {
      errors.general =
        "You appear to be offline. Please check your internet connection.";
    }

    if (isAccountLocked) {
      const minutes = Math.ceil(lockoutTimeLeft / 60000);
      errors.general = `Account temporarily locked. Try again in ${minutes} minutes.`;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ---- Enhanced error mapping ----
  const mapFirebaseLoginError = (err: any): string => {
    const code = String(err?.code || "");
    if (code.includes("auth/user-not-found"))
      return "No account found for this email address.";
    if (code.includes("auth/wrong-password"))
      return "Incorrect email or password.";
    if (code.includes("auth/invalid-credential"))
      return "Invalid email or password.";
    if (code.includes("auth/user-disabled"))
      return "Your account has been disabled. Please contact support.";
    if (code.includes("auth/too-many-requests"))
      return "Too many failed attempts. Please wait before trying again.";
    if (code.includes("auth/network-request-failed"))
      return "Network error. Please check your connection and try again.";
    if (code.includes("auth/invalid-email"))
      return "Please enter a valid email address.";
    return "Login failed. Please check your credentials and try again.";
  };

  const mapGoogleError = (err: any): string => {
    // Google itself worked and OUR server refused the account (not a member
    // of the institute whose domain this is, institute unavailable, ...): its
    // message says why, which "Google Sign-in failed" never did.
    const server = err?.response?.data;
    const serverMessage = typeof server?.message === "string" ? server.message : typeof server?.error === "string" ? server.error : "";
    if (err?.response?.status === 403 && serverMessage) return serverMessage;
    const code = String(err?.code || err?.message || "");
    if (code.includes("auth/popup-blocked"))
      return "Popup was blocked. Please allow popups and try again.";
    if (code.includes("auth/cancelled-popup-request"))
      return "Sign-in was cancelled. Please try again.";
    if (code.includes("auth/popup-closed-by-user"))
      return "Popup was closed before completing sign-in.";
    if (code.includes("auth/network-request-failed"))
      return "Network error. Please check your connection and try again.";
    if (code.includes("auth/account-exists-with-different-credential"))
      return "An account already exists with this email using a different sign-in method.";
    return "Google Sign-in failed. Please try again or use email/password.";
  };

  const mapBackendApiError = (err: any): string => {
    if (axios.isAxiosError(err)) {
      // Prefer backend-provided messages when available.
      if (err.response?.data?.message) {
        return err.response.data.message;
      }
      if (err.response?.data?.error) {
        return err.response.data.error;
      }
      if (err.response?.data?.detail) {
        return err.response.data.detail;
      }

      // Handle specific HTTP status codes
      if (err.response?.status === 401) {
        return "Invalid email or password. Please check your credentials.";
      }
      if (err.response?.status === 403) {
        return "Account access denied. Please contact support.";
      }
      if (err.response?.status === 404) {
        return "Login service not found. Please contact support.";
      }
      if (err.response?.status === 429) {
        return "Too many login attempts. Please wait before trying again.";
      }
      if (err.response?.status >= 500) {
        return "Server error. Please try again later.";
      }

      // Handle network errors
      if (err.code === "ECONNABORTED") {
        return "Request timeout. Please check your connection and try again.";
      }
      if (err.code === "ERR_NETWORK") {
        return "Network error. Please check your internet connection.";
      }
    }

    return "Login failed. Please try again.";
  };

  // ---- Resend verification functions ----
  const canResend = () => Date.now() - lastResendAtRef.current >= RESEND_MS;

  const startResendCountdown = () => {
    if (resendTimerRef.current) window.clearInterval(resendTimerRef.current);
    lastResendAtRef.current = Date.now();
    setResendLeftMs(RESEND_MS);
    resendTimerRef.current = window.setInterval(() => {
      const left = Math.max(
        0,
        RESEND_MS - (Date.now() - lastResendAtRef.current)
      );
      setResendLeftMs(left);
      if (left === 0 && resendTimerRef.current) {
        window.clearInterval(resendTimerRef.current);
        resendTimerRef.current = null;
      }
    }, 250);
  };

  const handleResendVerification = async () => {
    if (!emailNeedsVerification || !canResend()) return;

    try {
      await sendEmailVerification(emailNeedsVerification);
      startResendCountdown();
      toast({
        title: "Verification email sent",
        description: "Please check your inbox and spam folder.",
      });
    } catch (e: any) {
      const msg = String(e?.code || e?.message || "");
      const friendly = msg.includes("too-many-requests")
        ? "Too many verification requests. Please wait before trying again."
        : "Could not send verification email. Please try again later.";
      toast({ title: "Error", description: friendly, variant: "destructive" });
    }
  };

  // Format lockout time
  const formatLockoutTime = (ms: number): string => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  // cleanup on unmount — cancel pending axios calls + timers
  useEffect(() => {
    return () => {
      cancelTokens.current.forEach((src) => {
        try {
          src.cancel("Component unmounted");
        } catch { }
      });
      cancelTokens.current = [];
      if (resendTimerRef.current) window.clearInterval(resendTimerRef.current);
      if (lockoutTimerRef.current)
        window.clearInterval(lockoutTimerRef.current);
    };
  }, []);

  const getDeviceType = () => {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) return "Web Tablet";
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) return "Web Mobile";
    return "Web Desktop";
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setLoginData((prev) => ({ ...prev, [name]: value }));
    setError("");

    // Clear field-specific errors when user starts typing
    if (fieldErrors[name as keyof ValidationErrors]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }

    // Real-time validation for better UX
    if (name === "email" && value) {
      const emailError = validateEmail(normalizeEmail(value));
      if (emailError) {
        setFieldErrors((prev) => ({ ...prev, email: emailError }));
      }
    }
  };


  // ── Concurrent-login policy ──────────────────────────────────────────────
  // The backend answers 409 ACTIVE_SESSION_EXISTS when this account already has
  // a session on this device class. We ask the user, then replay the same login
  // with force_login set so the other device is signed out.
  const [sessionConflict, setSessionConflict] = useState<null | {
    device_name?: string | null;
    location?: string | null;
    last_active?: string | null;
    /** Sessions this class allows (browsers: 2) and currently holds. */
    limit?: number;
    active_count?: number;
  }>(null);

  /**
   * The sign-in response carries `signed_out_device` when this login displaced
   * another session — a full class now signs its oldest session out instead of
   * asking. Appended to the welcome toast so the person learns, on the device
   * in their hand, which of their devices just stopped working; everywhere else
   * that sign-out is silent.
   */
  const signedOutNote = (data: any) => {
    const note = data?.signed_out_device?.message;
    return typeof note === "string" && note ? ` ${note}.` : "";
  };

  /** The 409 payload, flattened for the dialog. */
  const conflictFrom = (data: any) => ({
    ...(data?.existing_session || {}),
    limit: data?.limit,
    active_count: data?.active_count,
  });
  const forceLoginRef = useRef(false);
  const retryLoginRef = useRef<null | (() => void | Promise<void>)>(null);

  const isActiveSessionConflict = (err: any) =>
    axios.isAxiosError(err) &&
    err.response?.status === 409 &&
    err.response?.data?.code === "ACTIVE_SESSION_EXISTS";

  // Set by the global axios interceptor when this device's session was taken
  // over from elsewhere, so the user learns why they were signed out.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const signedOut = params.get("signedOut");
    if (signedOut === "other-device") {
      setError(
        "You were signed out because this account was used on another device.",
      );
    } else if (signedOut === "access-refused") {
      // Set by services/tenantHostAccess: the server refused this session for
      // an institute reason (not a member here, or master switched it off).
      // The server's sentence names the institute; keep it.
      let message = "";
      try {
        message = sessionStorage.getItem(ACCESS_REFUSED_MESSAGE_KEY) || "";
        sessionStorage.removeItem(ACCESS_REFUSED_MESSAGE_KEY);
      } catch {
        // storage unavailable - generic sentence below
      }
      setError(
        message ||
          "Your access to this institute is not available. Please contact your institute administrator.",
      );
    }
    if (signedOut) {
      params.delete("signedOut");
      const query = params.toString();
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${query ? `?${query}` : ""}`,
      );
    }
  }, []);

  const confirmForceLogin = async () => {
    const retry = retryLoginRef.current;
    setSessionConflict(null);
    if (!retry) return;
    forceLoginRef.current = true;
    try {
      await retry();
    } finally {
      forceLoginRef.current = false;
      retryLoginRef.current = null;
    }
  };

  const handleEmailLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});
    setEmailNeedsVerification(null);

    if (!validate()) return;

    setIsLoading(true);
    const auth = getAuth();

    // Create cancel token for backend API call
    const source = axios.CancelToken.source();
    cancelTokens.current.push(source);

    try {
      const email = normalizeEmail(loginData.email);
      const password = loginData.password.trim();

      // First, authenticate with Firebase
      const cred = await signInWithEmailAndPassword(auth, email, password);

      // If not verified, show banner + offer resend
      if (!cred.user.emailVerified) {
        setEmailNeedsVerification(cred.user);
        toast({
          title: "Email not verified",
          description:
            "Please verify your email to continue. Check your inbox for the verification link.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      // Get Firebase ID token
      const firebaseToken = await cred.user.getIdToken();

      // Prepare data for backend API call
      const loginPayload = {
        email: email,
        name: cred.user.displayName || undefined,
        firebase_token: firebaseToken,
        function_type: "Login",
        login_from: "Email",
        force_login: forceLoginRef.current,
      };

      // Call backend API to validate and get user data
      const apiResponse = await axios.post(
        `${baseURL}/public/SignInEmail/`,
        loginPayload,
        {
          cancelToken: source.token,
          headers: {
            "Content-Type": "application/json",
            'X-Device-Type': getDeviceType(),
            // Identifies this browser so a re-login from it reclaims its own
            // session slot instead of being asked to sign out "the other
            // device" - which was this same browser's abandoned session.
            'X-Device-Id': getDeviceUid()
          }
        }
      );

      if (apiResponse.status === 200 && apiResponse.data) {
        const userData = apiResponse.data;

        // Check if 2FA is required
        if (userData["2fa_required"] === true) {
          // Store user_id and session_id for 2FA verification
          if (userData.user_id) {
            localStorage.setItem("2fa_user_id", userData.user_id.toString());
          }
          if (userData.session_id) {
            localStorage.setItem("2fa_session_id", userData.session_id);
          }
          // Store timestamp for 10-minute validation
          localStorage.setItem("2fa_timestamp", Date.now().toString());

          toast({
            title: "2FA Required",
            description: "Please enter the verification code sent to your email.",
          });

          // Navigate to 2FA page and preserve the intended post-login target.
          navigate(`${accountHref("/2fa")}?redirect=${encodeURIComponent(getPostLoginRedirect())}`);
          return;
        }

        // Clear failed attempts on successful login
        clearFailedAttempts();

        // Store tokens using unified token service
        try {
          // Store access_token and refresh_token in localStorage
          localStorage.setItem("access_token", userData.access);
          localStorage.setItem("refresh_token", userData.refresh);

          // Calculate expiration time from expires_in (in seconds) minus 2 minutes buffer
          const currentTime = Date.now();
          const bufferTime = 2 * 60 * 1000; // 2 minute buffer before actual expiry
          const effectiveExpiration =
            currentTime + userData.expires_in * 1000 - bufferTime;
          localStorage.setItem(
            "token_expiration",
            effectiveExpiration.toString()
          );
        } catch (storageError) {
          console.warn("Failed to store tokens:", storageError);
        }

        await completeLogin(
          "Welcome back! 🎉",
          `Successfully signed in as ${email}.${signedOutNote(userData)}`,
          email
        );
      } else {
        throw new Error("Backend authentication failed");
      }
    } catch (err: any) {
      if (isActiveSessionConflict(err)) {
        retryLoginRef.current = () => handleEmailLogin(e);
        setSessionConflict(conflictFrom(err.response?.data));
        return;
      }

      recordFailedAttempt();

      if (isNotMemberHere(err)) {
        setTenantNoAccess(normalizeEmail(loginData.email));
        return;
      }

      // Check if error is from backend API call or Firebase
      if (axios.isAxiosError(err) && !axios.isCancel(err)) {
        setError(mapBackendApiError(err));
      } else if (err?.code && err.code.includes("auth/")) {
        // Firebase authentication error
        setError(mapFirebaseLoginError(err));
      } else {
        // Generic error fallback
        setError("Login failed. Please check your credentials and try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Sends the Google credential to our backend and handles the outcome.
  //
  // Deliberately split out of handleGoogleLogin. The concurrent-login retry
  // replays whatever is in retryLoginRef, and that used to be the WHOLE of
  // handleGoogleLogin - which starts by calling signInWithGoogle(). So
  // confirming "Sign out other device & continue" reopened the Google popup and
  // made the user pick their account a second time, for a credential they had
  // just supplied. Replaying only this half reuses the credential already in
  // hand, so the confirmation goes straight through.
  const submitGoogleAuth = useCallback(
    async (payload: any) => {
      const source = axios.CancelToken.source();
      cancelTokens.current.push(source);
      setIsGoogleLoading(true);
      setError("");
      try {
      const apiResponse = await axios.post(
        `${baseURL}/public/google-auth/`,
        { ...payload, force_login: forceLoginRef.current },
        {
          cancelToken: source.token,
          headers: {
            'X-Device-Type': getDeviceType(),
            'X-Device-Id': getDeviceUid()
          }
        }
      );

      if (apiResponse.status === 200 && apiResponse.data) {
        const userData = apiResponse.data;

        // Check if 2FA is required
        if (userData["2fa_required"] === true) {
          // Store user_id and session_id for 2FA verification
          if (userData.user_id) {
            localStorage.setItem("2fa_user_id", userData.user_id.toString());
          }
          if (userData.session_id) {
            localStorage.setItem("2fa_session_id", userData.session_id);
          }
          // Store timestamp for 10-minute validation
          localStorage.setItem("2fa_timestamp", Date.now().toString());

          toast({
            title: "2FA Required",
            description: "Please enter the verification code sent to your email.",
          });

          // Navigate to 2FA page and preserve the intended post-login target.
          navigate(`${accountHref("/2fa")}?redirect=${encodeURIComponent(getPostLoginRedirect())}`);
          return;
        }

        // Clear failed attempts on successful login
        clearFailedAttempts();

        // Store tokens using unified token service
        try {
          // Store access_token and refresh_token in localStorage
          localStorage.setItem("access_token", userData.access);
          localStorage.setItem("refresh_token", userData.refresh);

          // Calculate expiration time from expires_in (in seconds) minus 2 minutes buffer
          const currentTime = Date.now();
          const bufferTime = 2 * 60 * 1000; // 2 minute buffer before actual expiry
          const effectiveExpiration =
            currentTime + userData.expires_in * 1000 - bufferTime;
          localStorage.setItem(
            "token_expiration",
            effectiveExpiration.toString()
          );
        } catch (storageError) {
          console.warn("Failed to store tokens:", storageError);
        }

        await completeLogin(
          "Success",
          (userData.function_type === "Register"
            ? "Account created successfully with Google!"
            : "Successfully signed in with Google!") + signedOutNote(userData),
          // The picker and no-access dialogs need the email; the backend
          // returns it with the token pair. Without it the Google path left
          // the context rows with a blank subtitle.
          userData.email ?? null
        );
      } else {
        setError("Google Sign-in failed. Please try again.");
      }
    } catch (error: any) {
      if (isActiveSessionConflict(error)) {
        // Retry this call only - the Google credential in `payload` is still
        // valid, so there is nothing to re-authenticate.
        retryLoginRef.current = () => submitGoogleAuth(payload);
        setSessionConflict(conflictFrom(error.response?.data));
        return;
      }

      if (isNotMemberHere(error)) {
        setTenantNoAccess(payload?.email ?? null);
        return;
      }

      if (!axios.isCancel(error)) {
        if (error.code?.includes("auth/")) {
          recordFailedAttempt();
        }
        setError(mapGoogleError(error));
      }
    } finally {
      setIsGoogleLoading(false);
    }
    },
    // completeLogin is listed now: it was missing, and only worked while it
    // happened to change in step with the router-derived callbacks above.
    [navigate, getPostLoginRedirect, completeLogin],
  );

  const handleGoogleLogin = useCallback(async () => {
    if (isAccountLocked || !isOnline) return;

    setIsGoogleLoading(true);
    setError("");
    try {
      const response = await signInWithGoogle();

      if (!response || !response.token || !response.result?.user?.email) {
        throw new Error("Invalid Google sign-in response");
      }

      const { token, result } = response;
      const updatedSignupData = {
        ...signupdata,
        function_type: "Register",
        login_from: "Google",
        name: result.user.displayName ?? "",
        email: normalizeEmail(result.user.email ?? ""),
        google_token: token,
      };

      setsignupdata(updatedSignupData);

      await submitGoogleAuth(updatedSignupData);
    } catch (error: any) {
      // Only failures of the Google popup itself land here; anything from the
      // backend call is handled inside submitGoogleAuth.
      if (!axios.isCancel(error)) {
        if (error.code?.includes("auth/")) {
          recordFailedAttempt();
        }
        setError(mapGoogleError(error));
      }
      setIsGoogleLoading(false);
    }
  }, [signupdata, isAccountLocked, isOnline, submitGoogleAuth]);

  // Helper variables for form state
  const isFormDisabled =
    isLoading || isGoogleLoading || isAccountLocked || !isOnline;

  // One error slot, rendered on whichever path is showing. Both `handleEmailLogin`
  // and `handleGoogleLogin` write to `error`, so it cannot live inside the form
  // any more.
  const inlineError =
    error || fieldErrors.general ? (
      <div className="login-inline-error">
        <AlertTriangle className="login-inline-error-icon" />
        <span>{fieldErrors.general || error}</span>
      </div>
    ) : null;

  return (
    <div className="login-container">
      {/* Background Pattern */}
      <div className="login-background" />

      {/* Already signed in on another device — confirm before taking over */}
      <AlertDialog
        open={!!sessionConflict}
        onOpenChange={(open) => {
          if (!open) {
            setSessionConflict(null);
            retryLoginRef.current = null;
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Already signed in elsewhere</AlertDialogTitle>
            <AlertDialogDescription>
              {(sessionConflict?.limit ?? 1) > 1
                ? `This account is already signed in on ${sessionConflict?.active_count ?? sessionConflict?.limit} computers, which is the maximum. The oldest session, on`
                : "This account is currently signed in on"}
              {sessionConflict?.device_name
                ? ` ${sessionConflict.device_name}`
                : " another device"}
              {sessionConflict?.location ? ` (${sessionConflict.location})` : ""}
              {sessionConflict?.last_active
                ? `, last active ${new Date(
                    sessionConflict.last_active,
                  ).toLocaleString()}`
                : ""}
              {(sessionConflict?.limit ?? 1) > 1
                ? ", will be signed out. Continue here?"
                : ". Do you want to sign out that device and continue here?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmForceLogin}>
              {(sessionConflict?.limit ?? 1) > 1
                ? "Sign out oldest & continue"
                : "Sign out other device & continue"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Network Status Indicator */}
      {!isOnline && (
        <div className="fixed top-4 right-4 z-50 bg-red-500 text-white px-4 py-2 rounded-lg flex items-center gap-2">
          <WifiOff className="w-4 h-4" />
          <span>You're offline</span>
        </div>
      )}

      <div className="login-main">
        {/* Centered Login Form */}
        <div className="login-content">
          <div className="login-wrapper">
            {/* Logo - Always visible */}
            {/* The tenant's mark where there is one, the platform's otherwise.
                brandName falls back to "EduExpertAI", so this renders exactly
                as before on our own domain and while the lookup is in flight. */}
            <div className="login-logo-section">
              <div className="login-logo">
                <div className="login-logo-icon">
                  <img
                    src={brandLogoFailed ? platformLogo : brandLogo}
                    alt={brandName}
                    onError={() => setBrandLogoFailed(true)}
                  />
                </div>
                <h1 className="login-logo-text">{brandName}</h1>
              </div>
              {brandTagline ? (
                <p className="text-sm text-muted-foreground mt-1">{brandTagline}</p>
              ) : null}
            </div>

            <Card className="login-card">
              <CardHeader className="login-card-header">
                <CardTitle className="login-card-title">
                  {showEmailForm ? "Sign in with email" : "Sign In"}
                </CardTitle>
                {/* Only the email path gets a subtitle. On the default screen
                    the description used to say "Choose how you'd like to sign
                    in" — an instruction to make a choice, on a screen that now
                    presents no choice. */}
                {showEmailForm ? (
                  <CardDescription className="login-card-description">
                    Use your registered email address
                  </CardDescription>
                ) : null}
              </CardHeader>

              <CardContent className="login-card-content">
                {/* Account lockout — shown above everything, affects all options */}
                {isAccountLocked && (
                  <div className="login-banner login-banner--error">
                    <Shield className="login-banner-icon" />
                    <span>
                      Account locked. Try again in{" "}
                      <strong>{formatLockoutTime(lockoutTimeLeft)}</strong>
                    </span>
                  </div>
                )}

                {/* Email verification banner */}
                {emailNeedsVerification && (
                  <div className="login-banner login-banner--warn">
                    <Mail className="login-banner-icon" />
                    <div className="login-banner-body">
                      <span>Email not verified — check your inbox.</span>
                      <button
                        type="button"
                        className="login-banner-action"
                        onClick={handleResendVerification}
                        disabled={resendLeftMs > 0}
                      >
                        {resendLeftMs > 0
                          ? `Resend in ${Math.ceil(resendLeftMs / 1000)}s`
                          : "Resend email"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Google — always on the card, in the same place on both
                    views, so revealing the email form never moves it. On
                    arrival it is the ONLY control here, which is the point:
                    one large coloured mark, recognised without reading it. */}
                <div className="login-primary-auth">
                  <Button
                    variant="outline"
                    className="login-google-btn"
                    onClick={handleGoogleLogin}
                    disabled={isFormDisabled}
                  >
                    {isGoogleLoading ? (
                      <div className="login-google-spinner" />
                    ) : (
                      <FcGoogle className="login-google-icon" />
                    )}
                    Continue with Google
                  </Button>

                  {/* A Google failure has to land somewhere. The only error
                      slot used to be inside the email form, which is hidden on
                      the default view — the message would have gone nowhere. */}
                  {!showEmailForm && inlineError}

                  {!showEmailForm && (
                    <>
                      <div className="login-or-divider" role="separator">
                        <span>or</span>
                      </div>
                      <button
                        type="button"
                        className="login-alt-auth-link"
                        onClick={() => {
                          setError("");
                          setFieldErrors({});
                          setShowEmailForm(true);
                        }}
                        disabled={isFormDisabled}
                      >
                        <Mail className="login-alt-auth-icon" />
                        Sign in with email
                      </button>
                    </>
                  )}
                </div>

                {showEmailForm && (
                <div className="login-email-section">
                  <form onSubmit={handleEmailLogin} className="login-form">
                    <div className="login-field">
                      <Label htmlFor="email" className="login-label">
                        Email Address
                      </Label>
                      <div className="login-input-wrapper">
                        <Mail className="login-input-icon" />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="Enter your email address"
                          value={loginData.email}
                          onChange={handleInputChange}
                          className="login-input"
                          required
                          autoComplete="off"
                          disabled={isFormDisabled}
                          aria-invalid={!!fieldErrors.email}
                          aria-describedby={
                            fieldErrors.email ? "email-error" : undefined
                          }
                        />
                      </div>
                      {fieldErrors.email && (
                        <div id="email-error" className="login-field-error">
                          {fieldErrors.email}
                        </div>
                      )}
                    </div>

                    <div className="login-field">
                      <div className="login-field-header">
                        <Label htmlFor="password" className="login-label">
                          Password
                        </Label>
                        <Link to={accountHref("/forgot-password")} className="login-forgot-link">
                          Forgot password?
                        </Link>
                      </div>
                      <div className="login-input-wrapper">
                        <Lock className="login-input-icon" />
                        <Input
                          id="password"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          value={loginData.password}
                          onChange={handleInputChange}
                          className="login-input login-password-input"
                          required
                          autoComplete="new-password"
                          disabled={isFormDisabled}
                          aria-invalid={!!fieldErrors.password}
                          aria-describedby={
                            fieldErrors.password ? "password-error" : undefined
                          }
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="login-password-toggle"
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={isFormDisabled}
                        >
                          {showPassword ? (
                            <EyeOff className="login-toggle-icon" />
                          ) : (
                            <Eye className="login-toggle-icon" />
                          )}
                        </Button>
                      </div>
                      {fieldErrors.password && (
                        <div id="password-error" className="login-field-error">
                          {fieldErrors.password}
                        </div>
                      )}
                    </div>

                    {/* Inline error — shown near the submit button, not at the top */}
                    {inlineError}

                    <Button
                      type="submit"
                      className="login-submit-btn"
                      disabled={isFormDisabled}
                    >
                      {isLoading ? (
                        <>
                          <div className="login-submit-spinner" />
                          Signing in...
                        </>
                      ) : (
                        <>
                          Sign In
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </form>
                </div>
                )}
              </CardContent>

              <CardFooter className="login-footer">
                {/* Same as ours on an institute's own domain: the shared
                    account, then "No access yet" until the institute adds
                    them. Not on the path address (/institutes/<slug>/login),
                    which has no sign-up page of its own and would open ours. */}
                {(isPlatform || !onInstitutePathSite()) && (
                <div className="login-signup-text pt-2">
                  Don't have an account?{" "}
                  <Link
                    to={signupPath}
                    state={{ returnTo: getPostLoginRedirect() }}
                    className="login-signup-link"
                  >
                    Sign up for free
                  </Link>
                </div>
                )}

                {/* Required on any screen that can create an account, and
                    Google sign-in does exactly that on first use — so this
                    belongs on both views, not just the email one. */}
                <p className="login-legal-text">
                  By continuing you agree to our{" "}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="login-legal-link"
                  >
                    Terms &amp; Conditions
                  </a>{" "}
                  and{" "}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="login-legal-link"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </CardFooter>
            </Card>

          </div>
        </div>
      </div>

      <NoAccessDialog {...noAccessDialogProps} />
      <NoAccessDialog
        open={tenantNoAccess !== undefined}
        email={tenantNoAccess}
        instituteName={isPlatform ? undefined : brandName}
        onOpenChange={(open) => { if (!open) setTenantNoAccess(undefined); }}
      />

      <ChooseContextDialog {...contextDialogProps} />
    </div>
  );
};

export default Login;

/** The server's answer for "signed in, but not a member of this domain's institute". */
const isNotMemberHere = (err: any): boolean =>
  err?.response?.status === 403 && err?.response?.data?.code === "TENANT_HOST_FORBIDDEN";
